import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { st, classes } from './ComposerButton.st.css';
import {
  ComposerButtonHandle,
  ComposerButtonProps,
} from './ComposerButton.types';
import { ButtonCore } from '../Button/ButtonCore';
import { Tooltip } from '../Tooltip';
import { COLORS, SHAPES, dataHooks } from './ComposerButton.constants';
import { Text } from '../Text';
import { generateDataAttr } from '../utils/generateDataAttr';

const ComposerButton = forwardRef<ComposerButtonHandle, ComposerButtonProps>(
  (props, ref) => {
    const buttonRef = React.useRef<HTMLElement>(null);
    const [isEllipsisActive, setIsEllipsisActive] = useState(false);

    const {
      dataHook,
      label,
      children,
      shape = SHAPES.square,
      color = COLORS.standard,
      ellipsis = true,
      maxLines = 1,
      disabled,
      ...rest
    } = props;

    useImperativeHandle(ref, () => {
      return {
        focus() {
          buttonRef.current && buttonRef.current.focus();
        },
      };
    }, [buttonRef]);

    const onEllipsisStateChange = useCallback(
      (_isEllipsisActive: boolean) => {
        if (isEllipsisActive !== _isEllipsisActive) {
          setIsEllipsisActive(_isEllipsisActive);
        }
      },
      [isEllipsisActive],
    );

    return (
      <Tooltip
        className={st(classes.tooltip)}
        disabled={!isEllipsisActive}
        content={label}
        data-hook={dataHook}
      >
        <ButtonCore
          {...rest}
          ref={buttonRef}
          className={st(classes.root, { color, disabled })}
          disabled={disabled}
          {...generateDataAttr(props, ['shape', 'color'])}
          data-hook={dataHooks.button}
        >
          <div
            className={st(classes.iconContainer, { disabled, shape, color })}
          >
            {children}
          </div>
          <Text
            size="tiny"
            weight="thin"
            className={st(classes.label)}
            disabled={disabled}
            ellipsis={ellipsis}
            maxLines={maxLines}
            tooltipProps={{ disabled: true }}
            onEllipsisStateChange={onEllipsisStateChange}
          >
            {label}
          </Text>
        </ButtonCore>
      </Tooltip>
    );
  },
);

ComposerButton.displayName = 'ComposerButton';

export { ComposerButton };
